import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, ArrowDown, ArrowUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useLiveRefresh } from '@/contexts/WebSocketContext';
import { API_BASE } from '@/utils/api';
import { inkOn } from '@/theme/statusColors';
import { useTableStates } from '@/components/tables/useTableStates';
import { STATE_META, money } from '@/components/tables/tableStates';
import { OnboardingChecklist } from '@/components/onboarding/OnboardingChecklist';
import { shortName } from '../shared';

const greet = () => { const h = new Date().getHours(); return h >= 6 && h < 13 ? 'Buen día' : h >= 13 && h < 20 ? 'Buenas tardes' : 'Buenas noches'; };
const unitLabel = (u) => `${u.unit_type === 'umbrella' ? 'S' : u.unit_type === 'tent' ? 'C' : ''}${shortName(u.name)}`;
const hhmm = (iso) => new Date(iso).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });

// [clave, título, formato, "más es mejor"]
const METRICS = [
  ['sales', 'Ventas del día', money, true], ['orders', 'Pedidos', String, true], ['ticket', 'Ticket promedio', money, true],
  ['tables', 'Mesas atendidas', String, true], ['tips', 'Propinas', money, true], ['kitchen_minutes', 'Tiempo de cocina', v => (v == null ? '—' : `${v} min`), false],
];

const Metric = ({ k, title, fmt, up, data }) => {
  const ch = data.changes[k];
  const good = ch == null ? null : (ch >= 0) === up;
  return (
    <div className="m-card p-4" data-testid={`dash-metric-${k}`}>
      <p className="text-sm text-muted-foreground">{title}</p>
      <p className="text-2xl font-bold mt-1 tabular-nums">{fmt(data.metrics[k])}</p>
      <p className={`text-xs mt-1 inline-flex items-center gap-1 ${good == null ? 'text-muted-foreground' : good ? 'text-[#166534] dark:text-[#4ade80]' : 'text-[#b91c1c] dark:text-[#f87171]'}`}>
        {ch == null ? `Sin datos del ${data.compare_label}` : <>{ch >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}{Math.abs(ch)} % vs {data.compare_label}</>}
      </p>
    </div>
  );
};

const Card = ({ title, children, testid, className = '' }) => (
  <section className={`m-card p-5 ${className}`} data-testid={testid}><h2 className="text-lg font-bold mb-3">{title}</h2>{children}</section>
);

const SalesByHour = ({ rows }) => {
  const max = Math.max(1, ...rows.map(r => r.total));
  return (
    <div className="flex items-end gap-1.5 h-48" role="img" aria-label="Ventas por hora">
      {rows.map(r => (
        <div key={r.hour} className="flex-1 flex flex-col items-center gap-1 h-full justify-end" title={`${r.hour} h · ${money(r.total)}`}>
          <div className="w-full rounded-t bg-[#2563EB]" style={{ height: `${(r.total / max) * 100}%`, minHeight: r.total ? 4 : 0 }} />
          <span className="text-[11px] text-muted-foreground">{r.hour}</span>
        </div>
      ))}
    </div>
  );
};

const TopProducts = ({ rows }) => {
  const max = Math.max(1, ...rows.map(r => r.quantity));
  if (!rows.length) return <p className="text-sm text-muted-foreground">Todavía no hay pedidos hoy</p>;
  return (
    <ul className="space-y-3">
      {rows.map(r => (
        <li key={r.name}>
          <div className="flex justify-between text-sm"><span className="truncate">{r.name}</span><span className="font-semibold tabular-nums">{r.quantity}</span></div>
          <div className="h-2 mt-1 rounded bg-muted"><div className="h-2 rounded bg-[#2563EB]" style={{ width: `${(r.quantity / max) * 100}%` }} /></div>
        </li>
      ))}
    </ul>
  );
};

const LiveFloor = ({ units, onPick }) => (
  <div className="grid grid-cols-4 gap-2 max-h-72 overflow-y-auto pr-1" data-testid="dash-live-floor">
    {units.map(u => {
      const bg = STATE_META[u.state]?.color || '#64748b';
      return (
        <button key={u.unit_id} onClick={() => onPick(u)} title={`${u.name} · ${STATE_META[u.state]?.label || ''}`} aria-label={`${u.name}, ${STATE_META[u.state]?.label || ''}`} data-testid={`dash-unit-${u.unit_id}`}
          className="aspect-square rounded-lg font-bold text-base grid place-items-center" style={{ background: bg, color: inkOn(bg) }}>{unitLabel(u)}</button>
      );
    })}
    {!units.length && <p className="col-span-4 text-sm text-muted-foreground">No hay mesas cargadas</p>}
  </div>
);

export default function ModernDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { units } = useTableStates();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const load = useCallback(() => axios.get(`${API_BASE}/dashboard/today`).then(r => { setData(r.data); setFailed(false); }).catch(() => setFailed(true)), []);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(load, 60000);
  return (
    <div className="p-4 md:p-6 flex flex-col gap-5" data-testid="modern-dashboard">
      <OnboardingChecklist />
      <h1 className="text-3xl font-bold">{greet()}, {user?.role === 'super_admin' ? 'Super Admin' : user?.name?.split(' ')[0]}</h1>
      {!data && !failed && <p className="text-muted-foreground" data-testid="dash-loading">Cargando el resumen del día…</p>}
      {failed && !data && (
        <div className="m-card p-5 flex items-center justify-between gap-3" role="alert" data-testid="dash-error">
          <span>No pudimos cargar el resumen del día.</span>
          <Button variant="outline" onClick={load} data-testid="dash-retry">Reintentar</Button>
        </div>
      )}
      {data && <>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {METRICS.map(([k, t, f, up]) => <Metric key={k} k={k} title={t} fmt={f} up={up} data={data} />)}
        </div>
        <div className="grid lg:grid-cols-[1.4fr_1fr_1fr] gap-4">
          <Card title="Ventas por hora" testid="dash-sales-hour"><SalesByHour rows={data.sales_by_hour} /></Card>
          <Card title="Top 5 platos" testid="dash-top"><TopProducts rows={data.top_products} /></Card>
          <Card title="Salón en vivo" testid="dash-floor"><LiveFloor units={units} onPick={u => navigate(`/pos?unit=${u.unit_id}`)} /></Card>
        </div>
        <div className="grid lg:grid-cols-2 gap-4">
          <Card title="Actividad en vivo" testid="dash-activity">
            <ul className="divide-y divide-border text-sm">
              {data.activity.map((a, i) => <li key={i} className="py-2 flex gap-3"><span className="tabular-nums text-muted-foreground">{hhmm(a.at)}</span><span>{a.text}{a.who ? ` · ${a.who}` : ''}</span></li>)}
              {!data.activity.length && <li className="py-2 text-muted-foreground">Sin movimientos todavía</li>}
            </ul>
          </Card>
          <Card title="Alertas" testid="dash-alerts">
            <ul className="divide-y divide-border text-sm">
              {data.alerts.map((a, i) => <li key={i} className="py-2 flex items-center gap-2 text-[#b91c1c] dark:text-[#f87171]"><AlertTriangle className="w-4 h-4 shrink-0" />{a.text}</li>)}
              {!data.alerts.length && <li className="py-2 text-muted-foreground">Todo en orden</li>}
            </ul>
          </Card>
        </div>
      </>}
    </div>
  );
}
